import { format, isValid } from 'date-fns';
import Handlebars from '@/utils/handlebars';
import { safelyParseDate } from './date';
import { formatCurrency } from './constants';

const formatDateString = (value, fallback) => {
	const date = safelyParseDate(value);
	if (!date || !isValid(date)) return fallback;
	return format(date, 'dd/MM/yyyy');
};

const extractPolicyData = (policyData) => {
	const transactions = policyData?.transactions ?? [];
	let policies = policyData?.policies ?? [];

	policies = Array.from(new Set(policies.map((p) => p.Id))).map((id) =>
		policies.find((p) => p.Id === id),
	);

	return policies.map((policy) => ({
		...policy,
		transaction: transactions.find((t) => t.Id === policy.CurrentPolicy),
	}));
};

const formatDealNeedsAnalysis = (dealData) =>
	dealData?.needsAnalysis
		?.map((entry) => {
			const key = entry?.key ?? '';
			const value = entry?.value ?? '';
			if (key && value) return `${key}: ${value}`;
			return key || value;
		})
		.filter(Boolean)
		.join(', ') || '[Deal Needs Analysis]';

const formatJoinedList = (values, fallback) => {
	if (Array.isArray(values)) {
		return values.join(', ') || fallback;
	}
	return values ?? fallback;
};

const buildPolicyList = (dealData) =>
	extractPolicyData(dealData?.policies).map((policy) => ({
		policyNumber: policy?.PolicyNumber ?? '[Policy Number]',
		fromDate: formatDateString(policy?.FromDate, '[From Date]'),
		effectiveDate: formatDateString(policy?.EffectiveDate, '[Effective Date]'),
		toDate: formatDateString(policy?.ToDate, '[To Date]'),
		insurer: policy?.InsurerName ?? '[Insurer]',
		classOfRisk: policy?.ClassOfRisk ?? '[Class Of Risk]',
		description: policy?.Description ?? '[Policy Description]',
		invoiceNumber: policy?.transaction?.InvoiceNumber ?? '[Invoice Number]',
		balance: policy?.Balance ? formatCurrency({ num: policy.Balance }) : '[Balance]',
		totalGrossPremium: policy?.transaction?.TotalGrossPremium
			? formatCurrency({ num: policy.transaction.TotalGrossPremium })
			: '[Total Gross Premium]',
	}));

/**
 * Builds the Handlebars context used to resolve Unlayer merge tags at send/export time.
 * Property paths mirror the `{{user.name}}`, `{{client.contactEmail}}`, `{{#policies}}` syntax
 * emitted by Unlayer templates.
 */
export const getUnlayerTagData = ({ user: userData, client: clientData, deal: dealData }) => {
	const user = {
		name: userData?.name ?? '[User Name]',
		phoneNumber: userData?.phoneNumber ?? '[User Phone Number]',
		email: userData?.username ?? userData?.email ?? '[User Email]',
		aboutUser: userData?.aboutUser ?? userData?.about ?? '[User About]',
		disclosureUrl: userData?.disclosureUrl ?? '[User Disclosure URL]',
		linkedIn: userData?.linkedIn ?? '[User LinkedIn]',
		fspNumber: userData?.fspNumber ?? '[User FSP Number]',
	};

	const brokerGroup = userData?.broker;
	const brokerage = {
		name: brokerGroup?.name ?? '[Brokerage Name]',
		address: brokerGroup?.address ?? '[Brokerage Address]',
		phoneNumber: brokerGroup?.phoneNumber ?? '[Brokerage Phone Number]',
		fspNumber: brokerGroup?.fspNumber ?? '[Brokerage FSP Number]',
		disclosureUrl: brokerGroup?.disclosureUrl ?? '[Brokerage Public Disclosure URL]',
		contactEmail: brokerGroup?.contactEmail ?? '[Brokerage Contact Email]',
		privacyStatement: brokerGroup?.privacyStatement ?? '[Brokerage Privacy Statement]',
		termsOfEngagement: brokerGroup?.termsOfEngagement ?? '[Brokerage Terms Of Engagement]',
		website: brokerGroup?.website ?? '[Brokerage Website]',
	};

	const client = {
		name: clientData?.name ?? '[Client Name]',
		contactFirstName: clientData?.contactFirstName ?? '[Client First Name]',
		contactLastName: clientData?.contactLastName ?? '[Client Last Name]',
		contactPhone: clientData?.contactPhone ?? '[Client Phone Number]',
		contactEmail: clientData?.contactEmail ?? '[Client Email]',
		industry: clientData?.industry ?? '[Client Industry]',
		numberOfEmployees: clientData?.numberOfEmployees ?? '[Client Number Of Employees]',
		annualRevenue: clientData?.annualRevenue ?? '[Client Annual Revenue]',
		// Legacy aliases so templates saved with the pre-migration tags still resolve.
		numberofemployees: clientData?.numberOfEmployees ?? '[Client Number Of Employees]',
		annualrevenue: clientData?.annualRevenue ?? '[Client Annual Revenue]',
		description: clientData?.description ?? '[Client Description]',
		addressLine1: clientData?.addressLine1 ?? '[Client Address Line 1]',
		addressLine2: clientData?.addressLine2 ?? '[Client Address Line 2]',
		addressLine3: clientData?.addressLine3 ?? '[Client Address Line 3]',
		suburb: clientData?.suburb ?? '[Client Suburb]',
		state: clientData?.state ?? '[Client State]',
		city: clientData?.city ?? '[Client City]',
		postcode: clientData?.postcode ?? '[Client Postcode]',
		country: clientData?.country ?? '[Client Country]',
	};

	const deal = {
		dealName: dealData?.dealName ?? '[Deal Name]',
		clientType: dealData?.clientType ?? '[Deal Client Type]',
		goalsOfCover: formatJoinedList(dealData?.goalsOfCover, '[Deal Goals Of Cover]'),
		needsAnalysis: formatDealNeedsAnalysis(dealData),
		riskClasses: formatJoinedList(dealData?.riskClasses, '[Deal Risk Classes]'),
		renewalDate: formatDateString(dealData?.renewalDate, '[Deal Renewal Date]'),
		additionalSpecialistProducts:
			dealData?.additionalSpecialistProducts ?? '[Deal Additional Specialist Products]',
	};

	const claim = {
		dealName: dealData?.dealName ?? '[Claim Name]',
		claimNumber: dealData?.claimNumber ?? '[Claim Number]',
		claimType: dealData?.claimType ?? '[Claim Type]',
		dateOfLoss: formatDateString(
			dealData?.LossDate ?? dealData?.dateOfLoss,
			'[Claim Date Of Loss]',
		),
		insurer: dealData?.InsurerName ?? dealData?.insurer ?? '[Claim Insurer]',
		Description:
			dealData?.Description ?? dealData?.PolicyDescription ?? '[Claim Description Of Loss]',
		PolicyNumber: dealData?.PolicyNumber ?? dealData?.policyNumber ?? '[Claim Policy Number]',
	};

	return {
		policies: buildPolicyList(dealData),
		user,
		brokerage,
		client,
		deal,
		claim,
	};
};

/**
 * Gets all supported merge tags for Unlayer.
 * Tag samples are hydrated from provided context values and fallback to
 * sensible placeholders when context is unavailable.
 */
export function getUnlayerTags(userDetails, clientDetails, dealDetails) {
	const tagData = getUnlayerTagData({
		user: userDetails,
		client: clientDetails,
		deal: dealDetails,
	});
	const policyList = tagData.policies;

	const userTags = {
		user_name: {
			name: '[User] Name',
			value: '{{user.name}}',
			sample: tagData.user.name,
		},
		user_phoneNumber: {
			name: '[User] Phone Number',
			value: '{{user.phoneNumber}}',
			sample: tagData.user.phoneNumber,
		},
		user_email: {
			name: '[User] Email',
			value: '{{user.email}}',
			sample: tagData.user.email,
		},
		user_about: {
			name: '[User] About',
			value: '{{user.aboutUser}}',
			sample: tagData.user.aboutUser,
		},
		user_disclosureUrl: {
			name: '[User] Disclosure URL',
			value: '{{user.disclosureUrl}}',
			sample: tagData.user.disclosureUrl,
		},
		user_linkedIn: {
			name: '[User] LinkedIn',
			value: '{{user.linkedIn}}',
			sample: tagData.user.linkedIn,
		},
		user_fspNumber: {
			name: '[User] FSP Number',
			value: '{{user.fspNumber}}',
			sample: tagData.user.fspNumber,
		},
	};

	const brokerageTags = {
		brokerage_name: {
			name: '[Brokerage] Name',
			value: '{{brokerage.name}}',
			sample: tagData.brokerage.name,
		},
		brokerage_address: {
			name: '[Brokerage] Address',
			value: '{{brokerage.address}}',
			sample: tagData.brokerage.address,
		},
		brokerage_phoneNumber: {
			name: '[Brokerage] Phone Number',
			value: '{{brokerage.phoneNumber}}',
			sample: tagData.brokerage.phoneNumber,
		},
		brokerage_fspNumber: {
			name: '[Brokerage] FSP Number',
			value: '{{brokerage.fspNumber}}',
			sample: tagData.brokerage.fspNumber,
		},
		brokerage_disclosureUrl: {
			name: '[Brokerage] Public Disclosure URL',
			value: '{{brokerage.disclosureUrl}}',
			sample: tagData.brokerage.disclosureUrl,
		},
		brokerage_contactEmail: {
			name: '[Brokerage] Contact Email',
			value: '{{brokerage.contactEmail}}',
			sample: tagData.brokerage.contactEmail,
		},
		brokerage_privacyStatement: {
			name: '[Brokerage] Privacy Statement',
			value: '{{brokerage.privacyStatement}}',
			sample: tagData.brokerage.privacyStatement,
		},
		brokerage_termsOfEngagement: {
			name: '[Brokerage] Terms Of Engagement',
			value: '{{brokerage.termsOfEngagement}}',
			sample: tagData.brokerage.termsOfEngagement,
		},
		brokerage_website: {
			name: '[Brokerage] Website',
			value: '{{brokerage.website}}',
			sample: tagData.brokerage.website,
		},
	};

	const clientTags = {
		client_name: {
			name: '[Client] Name',
			value: '{{client.name}}',
			sample: tagData.client.name,
		},
		client_firstName: {
			name: '[Client] First Name',
			value: '{{client.contactFirstName}}',
			sample: tagData.client.contactFirstName,
		},
		client_lastName: {
			name: '[Client] Last Name',
			value: '{{client.contactLastName}}',
			sample: tagData.client.contactLastName,
		},
		client_phoneNumber: {
			name: '[Client] Phone Number',
			value: '{{client.contactPhone}}',
			sample: tagData.client.contactPhone,
		},
		client_email: {
			name: '[Client] Email',
			value: '{{client.contactEmail}}',
			sample: tagData.client.contactEmail,
		},
		client_industry: {
			name: '[Client] Industry',
			value: '{{client.industry}}',
			sample: tagData.client.industry,
		},
		client_numberOfEmployees: {
			name: '[Client] Number Of Employees',
			value: '{{client.numberOfEmployees}}',
			sample: tagData.client.numberOfEmployees,
		},
		client_annualRevenue: {
			name: '[Client] Annual Revenue',
			value: '{{client.annualRevenue}}',
			sample: tagData.client.annualRevenue,
		},
		client_description: {
			name: '[Client] Description',
			value: '{{client.description}}',
			sample: tagData.client.description,
		},
		client_address: {
			name: '[Client] Address',
			mergeTags: {
				client_addressLine1: {
					name: '[Client Address] Line 1',
					value: '{{client.addressLine1}}',
					sample: tagData.client.addressLine1,
				},
				client_addressLine2: {
					name: '[Client Address] Line 2',
					value: '{{client.addressLine2}}',
					sample: tagData.client.addressLine2,
				},
				client_addressLine3: {
					name: '[Client Address] Line 3',
					value: '{{client.addressLine3}}',
					sample: tagData.client.addressLine3,
				},
				client_suburb: {
					name: '[Client Address] Suburb',
					value: '{{client.suburb}}',
					sample: tagData.client.suburb,
				},
				client_state: {
					name: '[Client Address] State',
					value: '{{client.state}}',
					sample: tagData.client.state,
				},
				client_city: {
					name: '[Client Address] City',
					value: '{{client.city}}',
					sample: tagData.client.city,
				},
				client_postcode: {
					name: '[Client Address] Postcode',
					value: '{{client.postcode}}',
					sample: tagData.client.postcode,
				},
				client_country: {
					name: '[Client Address] Country',
					value: '{{client.country}}',
					sample: tagData.client.country,
				},
			},
		},
	};

	const dealTags = {
		deal_name: {
			name: '[Deal] Name',
			value: '{{deal.dealName}}',
			sample: tagData.deal.dealName,
		},
		deal_clientType: {
			name: '[Deal] Client Type',
			value: '{{deal.clientType}}',
			sample: tagData.deal.clientType,
		},
		deal_goalsOfCover: {
			name: '[Deal] Goals Of Cover',
			value: '{{deal.goalsOfCover}}',
			sample: tagData.deal.goalsOfCover,
		},
		deal_needsAnalysis: {
			name: '[Deal] Needs Analysis',
			value: '{{deal.needsAnalysis}}',
			sample: tagData.deal.needsAnalysis,
		},
		deal_riskClasses: {
			name: '[Deal] Risk Classes',
			value: '{{deal.riskClasses}}',
			sample: tagData.deal.riskClasses,
		},
		deal_AdditionalSpecialistProducts: {
			name: '[Deal] Additional Specialist Products',
			value: '{{deal.additionalSpecialistProducts}}',
			sample: tagData.deal.additionalSpecialistProducts,
		},
		deal_renewalDate: {
			name: '[Deal] Renewal Date',
			value: '{{deal.renewalDate}}',
			sample: tagData.deal.renewalDate,
		},
	};

	const claimTags = {
		claim_name: {
			name: '[Claim] Name',
			value: '{{claim.dealName}}',
			sample: tagData.claim.dealName,
		},
		claim_claimNumber: {
			name: '[Claim] Claim Number',
			value: '{{claim.claimNumber}}',
			sample: tagData.claim.claimNumber,
		},
		claim_claimType: {
			name: '[Claim] Claim Type',
			value: '{{claim.claimType}}',
			sample: tagData.claim.claimType,
		},
		claim_dateOfLoss: {
			name: '[Claim] Date Of Loss',
			value: '{{claim.dateOfLoss}}',
			sample: tagData.claim.dateOfLoss,
		},
		claim_insurer: {
			name: '[Claim] Insurer',
			value: '{{claim.insurer}}',
			sample: tagData.claim.insurer,
		},
		claim_descriptionOfLoss: {
			name: '[Claim] Description Of Loss',
			value: '{{claim.Description}}',
			sample: tagData.claim.Description,
		},
		claim_policyNumber: {
			name: '[Claim] Policy Number',
			value: '{{claim.PolicyNumber}}',
			sample: tagData.claim.PolicyNumber,
		},
	};

	const policyTags = {
		policyNumber: {
			name: 'Policy Number',
			value: '{{policyNumber}}',
			sample: policyList[0]?.policyNumber ?? '[Policy Number]',
		},
		fromDate: {
			name: 'From Date',
			value: '{{fromDate}}',
			sample: policyList[0]?.fromDate ?? '[From Date]',
		},
		effectiveDate: {
			name: 'Effective Date',
			value: '{{effectiveDate}}',
			sample: policyList[0]?.effectiveDate ?? '[Effective Date]',
		},
		toDate: {
			name: 'To Date',
			value: '{{toDate}}',
			sample: policyList[0]?.toDate ?? '[To Date]',
		},
		insurer: {
			name: 'Insurer',
			value: '{{insurer}}',
			sample: policyList[0]?.insurer ?? '[Insurer]',
		},
		classOfRisk: {
			name: 'Class Of Risk',
			value: '{{classOfRisk}}',
			sample: policyList[0]?.classOfRisk ?? '[Class Of Risk]',
		},
		description: {
			name: 'Policy Description',
			value: '{{description}}',
			sample: policyList[0]?.description ?? '[Policy Description]',
		},
		invoiceNumber: {
			name: 'Invoice Number',
			value: '{{invoiceNumber}}',
			sample: policyList[0]?.invoiceNumber ?? '[Invoice Number]',
		},
		balance: {
			name: 'Balance',
			value: '{{balance}}',
			sample: policyList[0]?.balance ?? '[Balance]',
		},
		totalGrossPremium: {
			name: 'Total Gross Premium',
			value: '{{totalGrossPremium}}',
			sample: policyList[0]?.totalGrossPremium ?? '[Total Gross Premium]',
		},
	};

	return {
		policies: {
			name: 'Policies',
			rules: {
				repeat: {
					name: 'Repeat for Each Policy',
					before: '{{#policies}}',
					after: '{{/policies}}',
					sample: policyList,
				},
			},
			mergeTags: policyTags,
		},
		user: {
			name: 'User',
			mergeTags: userTags,
		},
		brokerage: {
			name: 'Brokerage',
			mergeTags: brokerageTags,
		},
		client: {
			name: 'Client',
			mergeTags: clientTags,
		},
		deal: {
			name: 'Deal',
			mergeTags: dealTags,
		},
		claim: {
			name: 'Claim',
			mergeTags: claimTags,
		},
	};
}

const normalizeHandlebarsTemplate = (htmlString) =>
	htmlString.replace(/>\s+</g, '><').replace(/\n/g, '');

const decodeMergeTagEntities = (htmlString) =>
	htmlString
		.replace(/&#123;/g, '{')
		.replace(/&#125;/g, '}')
		.replace(/&lcub;/gi, '{')
		.replace(/&rcub;/gi, '}');

const compileHandlebarsTemplate = (templateString, tagData) => {
	const template = Handlebars.compile(normalizeHandlebarsTemplate(templateString), {
		noEscape: true,
	});
	return template(tagData);
};

/**
 * Given html/text and Unlayer tag data, returns a string with merge tags resolved via Handlebars.
 */
export function replacePlaceholderTagsInHtmlString(htmlString, tagData) {
	if (typeof htmlString !== 'string' || htmlString.length === 0) return htmlString;
	if (!tagData || typeof tagData !== 'object') return htmlString;

	const preparedHtml = decodeMergeTagEntities(htmlString);

	try {
		return compileHandlebarsTemplate(preparedHtml, tagData);
	} catch (error) {
		console.error('Failed to render Unlayer merge tags', error);
		return htmlString;
	}
}

/**
 * Exports html from an initialized Unlayer editor instance.
 */
export function getUnlayerHtml(unlayer) {
	return new Promise((resolve, reject) => {
		if (!unlayer || typeof unlayer.exportHtml !== 'function') {
			reject(new Error('Unlayer editor is not ready'));
			return;
		}

		try {
			unlayer.exportHtml((data) => {
				console.log('11111', data);
				if (!data?.html) {
					reject(new Error('Unlayer exportHtml returned no HTML'));
					return;
				}

				resolve(data.html);
			});
		} catch (err) {
			reject(err);
		}
	});
}

const collectMergeTagDefinitions = (tags) =>
	Object.values(tags).flatMap((tagDef) => {
		if (!tagDef || typeof tagDef !== 'object') return [];
		if (typeof tagDef.value === 'string') return [tagDef];
		if (tagDef.mergeTags && typeof tagDef.mergeTags === 'object') {
			return collectMergeTagDefinitions(tagDef.mergeTags);
		}
		return [];
	});

/**
 * Replaces Unlayer merge-tag placeholders with sample values in HTML or plain text.
 */
export function replacePlaceholderTagsInHtml(html, unlayerTags) {
	if (typeof html !== 'string' || html.length === 0) return html;
	if (!unlayerTags || typeof unlayerTags !== 'object') return html;

	return collectMergeTagDefinitions(unlayerTags).reduce((resolvedHtml, tagDef) => {
		const token = tagDef?.value;
		if (typeof token !== 'string' || token.length === 0) {
			return resolvedHtml;
		}

		const replacement =
			tagDef?.sample === undefined || tagDef?.sample === null ? '' : String(tagDef.sample);
		return resolvedHtml.split(token).join(replacement);
	}, html);
}
